import { useState } from 'react';
import { ApiError, api } from '../api/client';
import { useAuth } from '../lib/auth';
import { Icon } from '../lib/icons';
import { teamSoundPrefs, type TeamSoundPrefs } from '../lib/teamSound';
import { previewTeamSound } from '../lib/teamSoundPlayer';
import { Notice } from './ui';

const VOLUMES: { id: TeamSoundPrefs['volume']; label: string }[] = [
  { id: 'low', label: 'Quiet' },
  { id: 'medium', label: 'Medium' },
  { id: 'high', label: 'Loud' }
];

/**
 * Your settings → Team texts (1.12.33): the sound for a text to a ring group or queue you answer
 * texts for, the spoken line after it, reminders while it waits, and how loud. Kept by Stitch for
 * the person (so they follow them to every device) and saved straight away.
 */
export function TeamTextSoundSettings() {
  const { state, signedIn } = useAuth();
  const [v, setV] = useState<TeamSoundPrefs>(() => teamSoundPrefs(state?.user?.teamTextSound));
  const [error, setError] = useState('');

  async function save(change: Partial<TeamSoundPrefs>) {
    const before = v;
    setV({ ...v, ...change });
    setError('');
    try {
      signedIn(await api.updateMe({ teamTextSound: change }));
    } catch (e) {
      setV(before);
      setError(e instanceof ApiError || e instanceof Error ? e.message : 'That didn’t save.');
    }
  }

  return (
    <fieldset className="plain-fieldset team-sound-settings" aria-describedby="team-sound-hint">
      <legend className="field-label">Team texts</legend>
      <span className="hint" id="team-sound-hint">
        When a text comes in for a ring group or queue you answer texts for. Saves straight away, and follows you to every device.
      </span>
      <div className="check sound-choice">
        <label className="check grow">
          <input type="checkbox" checked={v.sound} onChange={(e) => void save({ sound: e.target.checked })} />
          <span className="cell-stack">
            <span>Play the team text sound</span>
            <small className="muted">
              A counter bell, so you know a customer is waiting (stronger for urgent texts). Off: the message sound instead. Not on do not disturb or for the conversation you’re
              reading; one soft tone while you’re on a call.
            </small>
          </span>
        </label>
        <button type="button" className="link-btn small" onClick={() => void previewTeamSound(v)}>
          <Icon name="play" size={13} /> Play
        </button>
      </div>
      {v.sound && (
        <div className="indent-choices">
          <label className="check">
            <input type="checkbox" checked={v.speak} onChange={(e) => void save({ speak: e.target.checked })} />
            <span className="cell-stack">
              <span>Say which team it’s for</span>
              <small className="muted">“New text for Billing” after the sound (“Urgent text for Billing” for urgent ones). Never who texted or what they wrote.</small>
            </span>
          </label>
          <label className="check">
            <input type="checkbox" checked={v.reminders} onChange={(e) => void save({ reminders: e.target.checked })} />
            <span className="cell-stack">
              <span>Remind me while it waits</span>
              <small className="muted">The sound again every 30 seconds, up to 5 times, until someone takes it or answers it.</small>
            </span>
          </label>
          <label className="field team-sound-volume">
            <span className="field-label">Volume</span>
            <select className="input" value={v.volume} onChange={(e) => void save({ volume: e.target.value as TeamSoundPrefs['volume'] })}>
              {VOLUMES.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
      {error && <Notice tone="crit">{error}</Notice>}
    </fieldset>
  );
}
